import { ApolloError } from "@apollo/client";
import {
  ConsultantEventLogDataFragment,
  ConsultantEventType,
  useGetConsultantEventLogEventsQuery,
  useGetConsultantEventLogMetaDataQuery
} from "@bonliva-crm/graphql";
import {
  AlertDialog,
  Color,
  Modal,
  MultiSelect,
  SendIcon,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Card, Divider, Grid, makeStyles } from "@material-ui/core";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { AvailabilityCalendar } from "../availability-calendar/availability-calendar";
import { EmailEditor } from "../email-editor/email-editor";
import {
  EmailEditorFormValues,
  emailEditorFormSchema
} from "../email-editor/form-values";
import { GeneralInterest } from "../general-interest/general-interest";
import { EventList } from "./components/event-list";
import { EventLogEntry } from "./components/event-log-entry/event-log-entry";
import { FilterBadge } from "./components/filter-badge";
import { InputBadge } from "./components/input-badge";
import { useAddConsultantEventLogInterest } from "./hooks/add-consultant-event-log-interest";
import { useAddConsultantEventLogMail } from "./hooks/add-consultant-event-log-mail";
import { useAddConsultantEventLogNoAnswer } from "./hooks/add-consultant-event-log-no-answer";
import { createFilteredEventComponents } from "./utils/create-filtered-event-components";
import {
  EventLogActionType,
  EventLogInputType,
  EventLogTextType
} from "./utils/event-log-input-type";
import { FilterType, MainFilterType, OtherFilterType } from "./utils/filter-type";
import { groupEventsByFilterType } from "./utils/group-events-by-filter-type";
import { EventLogDataProvider, useEventLogData } from "./utils/refetch-consultant-events";
import { EventType } from "./utils/hook-props";
import { useGlobalStore, useLocale } from "@hooks";
import { SmsEventModal } from "./components/sms-event-modal";
import { translateSmsTextError } from "@components/sms/sms-text-hint";
import {
  createInitialMainFilters,
  initialMainFilters,
  initialOtherFilters
} from "./utils/initial-filters";

type Props = {
  consultantId: string;
};

const PAGE_SIZE = 20;

export const EventLog = (props: Props) => {
  const snack = useSnackbarsPipeline();
  const { locale } = useLocale();
  const store = useGlobalStore();
  const [alertOpen, setAlertOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [smsModalOpen, setSmsModalOpen] = useState(false);

  const [inputLoading, setInputLoading] = useState(false);
  const [fetchMoreIsLoading, setFetchMoreIsLoading] = useState(false);

  const currentPage = useRef(1);

  const [eventsGroupedByFilterType, setEventsGroupedByFilterType] = useState<Record<
    FilterType,
    ConsultantEventLogDataFragment[]
  > | null>(null);

  const [activeMainFilters, setActiveMainFilters] = useState(
    createInitialMainFilters(store.user.myProfile.AzureGroups)
  );
  const [activeOtherFilters, setActiveOtherFilters] = useState(initialOtherFilters);

  const activeFilters = [
    ...Object.entries(activeMainFilters),
    ...Object.entries(activeOtherFilters)
  ]
    .filter(([_, selected]) => selected)
    .map(([filter, _]) => filter as FilterType);

  useEffect(() => {
    currentPage.current = 1;
  }, [activeMainFilters, activeOtherFilters]);

  const { fetchMore, previousData, loading, data, refetch, variables } =
    useGetConsultantEventLogEventsQuery({
      variables: {
        consultantId: props.consultantId,
        limit: PAGE_SIZE,
        skip: (currentPage.current - 1) * PAGE_SIZE,
        type: activeFilters as ConsultantEventType[]
      },
      fetchPolicy: "cache-and-network",
      onError: (e) => {
        console.error(e);
        snack.showError(locale.ErrorOccurred);
      },
      onCompleted: (data) => {
        setEventsGroupedByFilterType(
          groupEventsByFilterType(data.consultantEvents.filter((e) => !e.Deleted))
        );
      },
      notifyOnNetworkStatusChange: true
    });

  const eventLogData = useEventLogData(refetch, variables);

  const getConsultantMetaDataQuery = useGetConsultantEventLogMetaDataQuery({
    variables: {
      consultantId: props.consultantId
    },
    fetchPolicy: "cache-first",
    onError: (e) => {
      console.error(e);
      snack.showError(locale.ErrorOccurred);
    }
  });

  const onError = (e?: ApolloError) => {
    // An SMS the API refused names the characters to remove (CRMDEV-7459).
    snack.showError(translateSmsTextError(e) ?? locale.cannotPerformAction);
    setInputLoading(false);
  };

  const onCompleted = (event?: EventType) => {
    snack.showSuccess(locale.eventAddedToEventLog);
    setInputLoading(false);
    setAlertOpen(false);
    eventLogData.refetchWithPolling(event?._id);
  };

  const onBeforeCall = () => {
    setInputLoading(true);
  };

  const { addConsultantEventLogNoAnswer } = useAddConsultantEventLogNoAnswer({
    onError,
    onCompleted,
    onBeforeCall
  });

  const { addConsultantEventLogInterest } = useAddConsultantEventLogInterest({
    onError,
    onCompleted,
    onBeforeCall
  });

  const { addConsultantEventLogMail } = useAddConsultantEventLogMail({
    onError,
    onCompleted,
    onBeforeCall
  });

  const emailEditorForm = useForm<EmailEditorFormValues>({
    defaultValues: {
      recipients: [
        {
          email: getConsultantMetaDataQuery.data?.consultantById?.Email,
          name: getConsultantMetaDataQuery.data?.consultantById?.Name
        }
      ],
      subject: null,
      body: null,
      attachments: []
    },
    resolver: yupResolver(emailEditorFormSchema)
  });

  const actionTypeActionMap: Record<EventLogActionType, () => void> = {
    [EventLogActionType.Noanswer]: () =>
      addConsultantEventLogNoAnswer({
        consultantId: props.consultantId
      }),
    [EventLogActionType.Email]: () => {
      setEmailModalOpen(true);
    },
    [EventLogActionType.SMS]: () => {
      setSmsModalOpen(true);
    },
    [EventLogActionType.Interest]: () => setAlertOpen(true)
  };

  const [activeInput, setActiveInput] = useState<EventLogInputType | null>(null);

  const filteredEventComponents = createFilteredEventComponents({
    filters: activeFilters,
    events: eventsGroupedByFilterType,
    onUpdated: () => {
      eventLogData.refetch();
    }
  });

  const otherFilterTypeOptions = useMemo(() => {
    return Object.values(OtherFilterType).map((filterType) => ({
      label: locale.consultantEventTypesNew[filterType],
      value: filterType
    }));
  }, []);

  const classes = useStyles();

  const providerData = useMemo(
    () => ({
      refetch,
      variables
    }),
    [refetch, variables]
  );

  const smsDisabledTooltipText = () => {
    if (!store.user.canSms) {
      return locale.entityHasMissingOrInvalidCellphone(locale.TheUser);
    } else if (!getConsultantMetaDataQuery.data?.consultantById.CellPhoneValid) {
      return locale.entityHasMissingOrInvalidCellphone(locale.TheConsultant);
    } else {
      return "";
    }
  };

  return (
    <EventLogDataProvider value={providerData}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Box style={{ display: "flex", flexDirection: "column", rowGap: "1rem" }}>
            <Card className={classes.inputContainer}>
              <Box className={classes.badgeContainer}>
                {Object.values(EventLogTextType).map((filterType, i) => (
                  <Fragment key={`filterType${i}`}>
                    <InputBadge
                      badgeType={filterType}
                      label={locale.consultantEventTypesNew[filterType]}
                      selected={activeInput === filterType}
                      setSelected={(selected) =>
                        setActiveInput(selected ? filterType : null)
                      }
                    />
                    {Object.values(EventLogTextType).length !== i && (
                      <Divider orientation="vertical" flexItem={true} />
                    )}
                  </Fragment>
                ))}

                {Object.values(EventLogActionType).map((filterType, i) => (
                  <Fragment key={`filterType${i}`}>
                    <InputBadge
                      badgeType={filterType}
                      label={locale.consultantEventTypesNew[filterType]}
                      onClick={actionTypeActionMap[filterType]}
                      disabledReason={
                        filterType === EventLogActionType.SMS
                          ? smsDisabledTooltipText()
                          : null
                      }
                    />
                  </Fragment>
                ))}

                <AlertDialog
                  open={alertOpen}
                  onOk={() =>
                    addConsultantEventLogInterest({
                      consultantId: props.consultantId
                    })
                  }
                  onClose={() => setAlertOpen(false)}
                  title={`${locale.continue}?`}
                  children={locale.interestEventConfirmation}
                />

                <SmsEventModal
                  consultantId={props.consultantId}
                  open={smsModalOpen}
                  onClose={() => setSmsModalOpen(false)}
                  onCompleted={onCompleted}
                  onError={onError}
                  onBeforeCall={onBeforeCall}
                />

                <Modal
                  dimensions={{ minWidth: "80%" }}
                  open={emailModalOpen}
                  setOpen={setEmailModalOpen}
                  onClose={() => setEmailModalOpen(false)}
                  modalContent={
                    <EmailEditor
                      form={emailEditorForm}
                      disabledFields={{ recipients: true }}
                    />
                  }
                  label={locale.SendMail}
                  saveButtonDisabled={false}
                  onSubmit={() => setEmailModalOpen(false)}
                  rightCta={
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        emailEditorForm.handleSubmit((data) =>
                          addConsultantEventLogMail({
                            consultantId: props.consultantId,
                            subject: data.subject,
                            body: data.body,
                            attachment: data.attachments.map(
                              (attachment) => attachment.NodeID
                            )
                          })
                        )();
                        emailEditorForm.reset();
                        setEmailModalOpen(false);
                      }}
                      endIcon={
                        <SendIcon
                          htmlColor={Color.White}
                          style={{ width: "0.75rem", height: "0.75rem" }}
                        />
                      }
                      disabled={!emailEditorForm.formState.isValid}
                    >
                      {locale.Send}
                    </Button>
                  }
                  disableEnforceFocus
                />
              </Box>
              <EventLogEntry
                consultantId={props.consultantId}
                activeInput={activeInput}
                setActiveInput={setActiveInput}
                loading={inputLoading}
                setLoading={setInputLoading}
                bookingStatus={
                  getConsultantMetaDataQuery.data?.consultantById.bookingStatus
                }
              />
            </Card>
            <Card style={{ minHeight: "500px" }}>
              <Box className={classes.eventTopBar}>
                <Box className={classes.badgeContainer}>
                  <FilterBadge
                    badgeType={null}
                    selected={activeFilters.length === 0}
                    setSelected={(selected) => {
                      if (selected) {
                        setActiveMainFilters(initialMainFilters);
                        setActiveOtherFilters(initialOtherFilters);
                      }
                    }}
                  />
                  <Divider
                    orientation="vertical"
                    flexItem={true}
                    style={{ margin: "6px 0" }}
                  />
                  {Object.values(MainFilterType).map((filterType) => (
                    <Fragment key={filterType}>
                      <FilterBadge
                        badgeType={filterType}
                        selected={activeMainFilters[filterType]}
                        setSelected={(selected) =>
                          setActiveMainFilters({
                            ...activeMainFilters,
                            [filterType]: selected
                          })
                        }
                      />
                      <Divider
                        orientation="vertical"
                        flexItem={true}
                        style={{ margin: "6px 0" }}
                      />
                    </Fragment>
                  ))}
                  <div style={{ minWidth: "200px" }}>
                    <MultiSelect<{ label: string; value: OtherFilterType }>
                      options={otherFilterTypeOptions}
                      value={Object.entries(activeOtherFilters)
                        .filter(([_, selected]) => selected)
                        .map(([filter, _]) => ({
                          label: locale.consultantEventTypesNew[filter],
                          value: filter as OtherFilterType
                        }))}
                      onChange={(selected) => {
                        setActiveOtherFilters(
                          selected.reduce(
                            (acc, filter) => ({ ...acc, [filter.value]: true }),
                            initialOtherFilters
                          )
                        );
                      }}
                      hideSelectedOptions={false}
                      closeMenuOnSelect={false}
                      optionType="checkbox"
                      styleProps={{
                        control: { showBorder: true },
                        menu: { showBorder: true }
                      }}
                      controlShouldRenderValue={false}
                      placeholder={locale.MiscFilters}
                      showBadge={Object.values(activeOtherFilters).some(
                        (filter) => filter
                      )}
                      isSearchable={false}
                      showOptionDivider={false}
                      hidePlaceholderOnFocus={false}
                    />
                  </div>
                </Box>
                <Divider />
              </Box>
              <EventList
                isLoading={loading && !previousData}
                isInputLoading={inputLoading}
                eventComponents={filteredEventComponents}
                fetchMore={() => {
                  setFetchMoreIsLoading(true);
                  currentPage.current++;
                  fetchMore({
                    variables: {
                      skip: (currentPage.current - 1) * PAGE_SIZE
                    }
                  }).finally(() => setFetchMoreIsLoading(false));
                }}
                fetchMoreIsLoading={fetchMoreIsLoading}
                canFetchMore={
                  data?.consultantEvents.length === currentPage.current * PAGE_SIZE
                }
              />
            </Card>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <AvailabilityCalendar consultantId={props.consultantId} />
          <GeneralInterest consultantId={props.consultantId} />
        </Grid>
      </Grid>
    </EventLogDataProvider>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "row",
    padding: "1rem"
  },
  inputContainer: {
    padding: "1rem 2rem",
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem"
  },
  eventTopBar: {
    padding: "1rem 1rem 1rem 2.5rem",
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem"
  },
  badgeContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "0.4rem",
    alignItems: "center",
    flexWrap: "wrap"
  }
}));
